import { IonContent, IonPage } from '@ionic/react';
import React, { ComponentProps } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getStoreEarnings } from '../../../../services/inventory/getStoreEarnings';
import Discount from './Discount';
import QuickAccessCard from '../../../../components/Dashboard/home/QuickAccess/QuickAccessCard';
import images from '../../../../assets/images';
import NotificationCards from '../../../../components/Dashboard/home/Notifications/NotificationCards';
import SalesTrendChart from '../../../../components/Dashboard/home/SalesTrendChart';
import { ProductInventoryType } from '../../../../types/inventory/ProductInventoryType';
import ProfileCompletion from '../../../../components/Dashboard/home/ProfileCompletion';
import DashboardOverview from '../../../../components/Dashboard/home/DashboardOverview';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import useStoreInventoryService from '../../../../services/inventory/hooks/useStoreInventoryService';
import { formatPrice } from '../../../../utils/formatPrice';
import EmptyPage from '../../../../components/ui/EmptyPage';
import Header from '../GettingStarted/Header';
import WalletOptions from '../../../../components/Dashboard/home/wallet/WalletOptions';

const quickAccess = [
  {
    image: images.addProductsIcon,
    title: 'add products',
    to: '/dashboard/catalog',
    description:
      'Buy Meds, Wellness, Supplements and other essential Products.',
  },
  {
    image: images.viewOrdersIcon,
    title: 'view orders',
    to: '/dashboard/orders',
    description:
      'Book a consultation with a doctor live on issues pertaining your health',
  },
  {
    image: images.createAdsIcon,
    title: 'create ads',
    to: '/dashboard/ads',
    description: 'Find a doctor for homecare for you or your loved ones.',
  },
  {
    image: images.checkChatIcon,
    title: 'check chat',
    to: '/dashboard/chat',
    description: 'Stay connected—recharge in seconds',
  },
];

const dummyNotifications: Array<
  React.ComponentProps<typeof NotificationCards>
> = [];

export default function index() {
  const { signedAccessToken: authToken } = useSelector(
    (state: RootState) => state.userSession
  );
  const activeStore = useSelector(
    (state: RootState) => state.userStores.activeStore
  );

  const {
    metrics: {
      data: overviewData,
      isLoading: overviewDataLoading,
      isFetching: overviewDataFetching,
      isError: overviewDataError,
      refetch: overviewDataRetch,
    },
    inventory: { data: inventoryData },
  } = useStoreInventoryService({
    authToken,
    storeId: activeStore?.id,
    target: 'both',
  });

  // Real bought-products sales trend: top sellers by units sold.
  const salesTrend = React.useMemo(() => {
    const items = (inventoryData?.data ?? []) as ProductInventoryType[];
    if (items.length === 0) return [];
    return [...items]
      .sort((a, b) => Number(b.units_sold ?? 0) - Number(a.units_sold ?? 0))
      .slice(0, 7)
      .map((p) => ({
        name: p.product_name,
        sales: Number(p.units_sold ?? 0),
        earnings: Number(p.earnings ?? 0),
      }));
  }, [inventoryData]);

  // Earnings split: held (pending delivery) vs available (delivered/cleared).
  const { data: earnings } = useQuery({
    queryKey: ['store-earnings', activeStore?.id],
    enabled: Boolean(authToken) && Boolean(activeStore?.id),
    queryFn: () => getStoreEarnings(activeStore!.id, authToken as string),
  });

  const inventoryAnalytics = React.useMemo(() => {
    const cards: ComponentProps<typeof DashboardOverview>['overview'] = [];
    if (overviewData) {
      const { sales, revenue } = overviewData;
      cards.push(
        {
          value: `${sales.current_period}`,
          type: 'total sales',
          theme: 'blue',
        },
        {
          value: formatPrice(revenue.current_period),
          type: 'revenue',
          theme: 'green',
        }
      );
    }
    if (earnings) {
      cards.push(
        {
          value: formatPrice(earnings.pending),
          type: 'expected (held)',
          theme: 'navy',
        },
        {
          value: formatPrice(earnings.available),
          type: 'available for payout',
          theme: 'default',
        }
      );
    }
    return cards.length
      ? cards
      : (undefined as ComponentProps<typeof DashboardOverview>['overview']);
  }, [overviewData, earnings]);

  return (
    <IonPage>
      <IonContent>
        <div className={`bg-[rgba(246,248,250,1)] space-y-6 pr-0 sm:pr-2`}>
          <div className="px-1">
            <Header
              logo={activeStore?.media?.logo?.file_url}
              storeName={activeStore?.store_name}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[2.75fr_2.25fr] gap-4">
            <div className="max-sm:order-2">
              <Discount />
            </div>
            <div className="max-sm:order-1">
              <WalletOptions />
            </div>
          </div>
          <ProfileCompletion />
          {/* OVERVIEW SECTION */}
          <DashboardOverview
            refetch={overviewDataRetch}
            overview={inventoryAnalytics}
            isLoading={overviewDataFetching && overviewDataLoading}
            isError={overviewDataError}
          />
          <div className="w-full h-[70vh]">
            <SalesTrendChart data={salesTrend} />
          </div>

          {/* QUICK ACCESS SECTION */}
          <div className="p-4 sm:p-6 space-y-4 rounded-lg bg-white border border-border shadow-sm">
            <div className="space-y-2">
              <h6 className="high-emphasis-text font-semibold">Quick Access</h6>
              <p className="medium-emphasis-text ">
                Discover the latest tools and features designed to enhance Swift
                experience
              </p>
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(12rem,1fr))] gap-4">
              {quickAccess.map((link, index) => (
                <QuickAccessCard {...link} key={index} />
              ))}
            </div>
          </div>

          {/* NOTIFICATION SECTION */}
          <div className="p-4 sm:p-6 space-y-4 rounded-lg bg-white border border-border shadow-sm">
            <div className="space-y-2">
              <h6 className="high-emphasis-text font-semibold">
                Notifications
              </h6>
              <p className="medium-emphasis-text ">
                Track and manage your reminders in one place.
              </p>
            </div>
            <div className="space-y-2">
              {dummyNotifications.length > 0 ? (
                dummyNotifications.map((notification) => (
                  <NotificationCards
                    {...notification}
                    key={notification.title}
                  />
                ))
              ) : (
                <EmptyPage message="No notification" className="h-[40vh]" />
              )}
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
